/** Guided mode (ADR-0023; content §2.3, §2.4): the copy and the pure pieces the guided screens read. */
import type { ContentCatalog, ForecastBandRange, TeamState } from "@bsim/contracts";
import type { ForecastBand, KpiId } from "@bsim/domain";
import { formatKpi } from "../../format";
import { BAND_LABELS, labelOf } from "../../format/labels";

/** The three called KPIs as guided copy names them (content §2.4 "KPI labels"). */
export const BAND_KPIS = [
  { kpi: "nim", label: labelOf("nim"), short: "Margin" },
  { kpi: "ncoRate", label: labelOf("ncoRate"), short: "Loan losses" },
  { kpi: "cet1Marked", label: labelOf("cet1Marked"), short: "Capital" },
] as const;

/** The three called measures in a one-line summary: the plain name first, its code in brackets (as the practice table reads them). */
export const CALL_NAME = { nim: labelOf("nim"), ncoRate: labelOf("ncoRate"), cet1Marked: labelOf("cet1Marked") } as const;

const bp = (value: number): string => `${value < 0 ? "−" : ""}${Math.abs(value)}`;

/** "below −2 basis points" · "−2 to 2 basis points" · "2–6 basis points" · "above 6 basis points": the range from the band's own bounds (null = open end), the unit said in words. */
export function bandRangeText({ lowerBp, upperBp }: ForecastBandRange): string {
  if (lowerBp === null) return upperBp === null ? "" : `below ${bp(upperBp)} basis points`;
  if (upperBp === null) return `above ${bp(lowerBp)} basis points`;
  return `${bp(lowerBp)}${lowerBp < 0 || upperBp < 0 ? " to " : "\u2013"}${bp(upperBp)} basis points`;
}

/** One edge in basis points: a true minus or an explicit plus, zero bare ("−2", "+6", "0"). */
const edgeBp = (value: number): string => (value === 0 ? "0" : `${value < 0 ? "\u2212" : "+"}${Math.abs(value)}`);

/** The band's range in one style everywhere: "−2 to +2 bp", "below −2 bp", "+2 to +6 bp", "above +6 bp" ("" for a band with no bounds). */
export function bandRangeShort({ lowerBp, upperBp }: ForecastBandRange): string {
  if (lowerBp === null) return upperBp === null ? "" : `below ${edgeBp(upperBp)} bp`;
  if (upperBp === null) return `above ${edgeBp(lowerBp)} bp`;
  return `${edgeBp(lowerBp)} to ${edgeBp(upperBp)} bp`;
}

/** The band's name and its range apart (the picker puts them on separate lines); `range` is "" when the range is not known. */
export function bandParts(band: ForecastBand, ranges?: readonly ForecastBandRange[] | null): { readonly name: string; readonly range: string } {
  const found = ranges?.find((r) => r.band === band);
  return { name: BAND_LABELS[band], range: found === undefined ? "" : bandRangeShort(found) };
}

/** "A little up (+2 to +6 bp)": the name and the range joined, the one wording the strip, the review and the memo share. The name alone when the range is not known. */
export function bandLabel(band: ForecastBand, ranges?: readonly ForecastBandRange[] | null): string {
  const { name, range } = bandParts(band, ranges);
  return range === "" ? name : `${name} (${range})`;
}

/**
 * The level a band implies on the measure's own basis: last year's value moved by the band's edges (the forecast is scored on
 * the move, edge in basis points over last year's value; 13.1 E1). "about 3.53% to 3.57%" · "below 3.53%" · "above 3.61%".
 */
export function impliedLevel(kpi: KpiId, lastYear: number, { lowerBp, upperBp }: ForecastBandRange): string {
  const at = (bpEdge: number): string => formatKpi(kpi, lastYear + bpEdge / 10_000);
  if (lowerBp === null) return upperBp === null ? "" : `below ${at(upperBp)}`;
  if (upperBp === null) return `above ${at(lowerBp)}`;
  return `about ${at(lowerBp)} to ${at(upperBp)}`;
}

/** The "new powers" copy (content §2.3): its heading, three "what's new" items and closing line, with the counts read from the catalog (every decision open, the focus ones the team already owned). */
export function newPowers(catalog: Pick<ContentCatalog, "decisions" | "focusDecisions">) {
  const total = catalog.decisions.length;
  const owned = Object.values(catalog.focusDecisions?.families ?? {}).reduce((n, f) => n + f.opening.length + f.later.length, 0);
  return {
    title: "New powers.",
    items: [
      `You now control all ${total} decisions; ${owned} carry over from last year.`,
      "The efficiency programme lasts one year and starts each year at none.",
    ],
    closing: "Change only what you have a reason to change.",
  };
}

/** First executive round after a guided one (screens §P6): the ease-in banner shows there, once. */
export const isFirstExecutiveRound = (team: TeamState): boolean => {
  const roundNo = team.round?.roundNo;
  const levels = team.cohort.complexityByRound;
  return roundNo !== undefined && levels[roundNo] === "executive" && levels[roundNo - 1] === "guided";
};
